import { useQuery } from "@tanstack/react-query";
import type { TApiODIN } from "@/apis/odin/apiODIN";
import { coaKeys } from "@/queries/queryKeys";
import { convertAccountsResponseToDTO } from "@/store/accounts/convertAccountsResponseToDTO";
import { convertToChartOfAccountsDTO } from "@/queries/coa/convertToChartOfAccountsDTO";
import { convertAccountsResponseToDTO as convertCustomAccountsToDTO } from "@/store/sliceCustomChartOfAccountsMappings/convertAccountsResponseToDTO";
import { convertMappingTableResponseToDTO } from "@/store/sliceCustomChartOfAccountsMappings/convertMappingTableResponseToDTO";

export const createCoaQueryHooks = ({ apiODIN }: { apiODIN: TApiODIN }) => {
    const useCoasByDatabaseQuery = (
        databaseId: number,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            queryKey: coaKeys.byDatabase(databaseId),
            queryFn: async () => {
                const response =
                    await apiODIN.getAvailableCoAsForDatabaseId(databaseId);
                return convertToChartOfAccountsDTO(response.data);
            },
            enabled: options?.enabled,
        });

    const useAllCoasForUserQuery = (options?: { enabled?: boolean }) =>
        useQuery({
            queryKey: coaKeys.allForUser(),
            queryFn: async () => {
                const response = await apiODIN.getAllAvailableCoAsForUser();
                return response.data;
            },
            enabled: options?.enabled,
        });

    const useCoaDefinitionQuery = (
        coaId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            queryKey: coaKeys.definition(coaId ?? -1),
            queryFn: async () => {
                if (coaId === null) throw new Error("coaId is required");
                const response = await apiODIN.getCoaDefinitionByCoaId(coaId);
                return response.data;
            },
            enabled: (options?.enabled ?? true) && coaId !== null,
        });

    const useAccountsByCoaQuery = (
        coaId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            queryKey: coaKeys.accounts(coaId ?? -1),
            queryFn: async () => {
                if (coaId === null) throw new Error("coaId is required");
                const response = await apiODIN.getApiAccountsByCoaId(coaId);
                return convertAccountsResponseToDTO(response.data);
            },
            enabled: (options?.enabled ?? true) && coaId !== null,
        });

    const useCoaMappingTableQuery = (
        coaId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            queryKey: coaKeys.mappingTable(coaId ?? -1),
            queryFn: async () => {
                if (coaId === null) throw new Error("coaId is required");
                const response =
                    await apiODIN.getApiCoaMappingTableByCoaId(coaId);
                const { customAccounts, mappingTable } = response.data;
                return {
                    customAccounts: convertCustomAccountsToDTO(customAccounts),
                    mappingTable:
                        convertMappingTableResponseToDTO(mappingTable),
                };
            },
            enabled: (options?.enabled ?? true) && coaId !== null,
        });

    const useCoaAuditLogsQuery = (
        coaId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            queryKey: coaKeys.auditLogs(coaId ?? -1),
            queryFn: async () => {
                if (coaId === null) throw new Error("coaId is required");
                const response = await apiODIN.getAuditLogsByCoaId(coaId);
                return response.data;
            },
            enabled: (options?.enabled ?? true) && coaId !== null,
        });

    return {
        useCoasByDatabaseQuery,
        useAllCoasForUserQuery,
        useCoaDefinitionQuery,
        useAccountsByCoaQuery,
        useCoaMappingTableQuery,
        useCoaAuditLogsQuery,
    };
};

export type TCoaQueryHooks = ReturnType<typeof createCoaQueryHooks>;
